import { useMemo } from "react";

const selCls = "bg-white/5 border border-white/15 rounded-lg px-2 py-2.5 text-sm text-white focus:outline-none focus:border-primary appearance-none";

/** 12-hour time picker: hour (1-12) + minute + AM/PM. Value stays "HH:MM" (24h). */
export const Time12Input = ({ value, onChange, testid }) => {
  const cur = useMemo(() => {
    if (!value) return { h: "", m: "00", ap: "AM" };
    const [H, M] = value.split(":").map(Number);
    return { h: String(H % 12 || 12), m: String(M || 0).padStart(2, "0"), ap: H >= 12 ? "PM" : "AM" };
  }, [value]);
  const set = (patch) => {
    const n = { ...cur, ...patch };
    if (!n.h) { onChange(""); return; }
    let H = parseInt(n.h, 10) % 12;
    if (n.ap === "PM") H += 12;
    onChange(`${String(H).padStart(2, "0")}:${n.m}`);
  };
  const minutes = ["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"];
  if (value && !minutes.includes(cur.m)) minutes.push(cur.m);
  return (
    <div className="flex gap-1" data-testid={testid}>
      <select value={cur.h} onChange={(e) => set({ h: e.target.value })} aria-label="Hour" data-testid={`${testid}-h`} className={`${selCls} flex-1 min-w-0`}>
        <option value="" className="bg-[#252720]">HH</option>
        {[...Array(12)].map((_, i) => <option key={i + 1} value={String(i + 1)} className="bg-[#252720]">{i + 1}</option>)}
      </select>
      <select value={cur.m} onChange={(e) => set({ m: e.target.value })} aria-label="Minutes" data-testid={`${testid}-m`} className={`${selCls} flex-1 min-w-0`}>
        {minutes.map((m) => <option key={m} value={m} className="bg-[#252720]">{m}</option>)}
      </select>
      <select value={cur.ap} onChange={(e) => set({ ap: e.target.value })} aria-label="AM or PM" data-testid={`${testid}-ap`} className={`${selCls} shrink-0`}>
        <option value="AM" className="bg-[#252720]">AM</option>
        <option value="PM" className="bg-[#252720]">PM</option>
      </select>
    </div>
  );
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** US-style date picker: MM / DD / YYYY selects. Value stays "YYYY-MM-DD". */
export const DateUSInput = ({ value, onChange, testid }) => {
  const [y, m, d] = (value || "").split("-");
  const now = new Date();
  const years = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];
  if (y && !years.includes(parseInt(y, 10))) years.unshift(parseInt(y, 10));
  const daysInMonth = y && m ? new Date(parseInt(y, 10), parseInt(m, 10), 0).getDate() : 31;
  const set = (ny, nm, nd) => {
    if (ny && nm && nd) {
      const dd = Math.min(parseInt(nd, 10), new Date(parseInt(ny, 10), parseInt(nm, 10), 0).getDate());
      onChange(`${ny}-${nm}-${String(dd).padStart(2, "0")}`);
    } else {
      onChange("");
    }
  };
  return (
    <div className="flex gap-1" data-testid={testid}>
      <select value={m || ""} onChange={(e) => set(y, e.target.value, d)} aria-label="Month" data-testid={`${testid}-mm`} className={`${selCls} flex-1 min-w-0`}>
        <option value="" className="bg-[#252720]">MM</option>
        {MONTHS.map((name, i) => <option key={i} value={String(i + 1).padStart(2, "0")} className="bg-[#252720]">{String(i + 1).padStart(2, "0")} — {name}</option>)}
      </select>
      <select value={d || ""} onChange={(e) => set(y, m, e.target.value)} aria-label="Day" data-testid={`${testid}-dd`} className={`${selCls} flex-1 min-w-0`}>
        <option value="" className="bg-[#252720]">DD</option>
        {[...Array(daysInMonth)].map((_, i) => <option key={i + 1} value={String(i + 1).padStart(2, "0")} className="bg-[#252720]">{i + 1}</option>)}
      </select>
      <select value={y || ""} onChange={(e) => set(e.target.value, m, d)} aria-label="Year" data-testid={`${testid}-yy`} className={`${selCls} shrink-0`}>
        <option value="" className="bg-[#252720]">YYYY</option>
        {years.map((yy) => <option key={yy} value={String(yy)} className="bg-[#252720]">{yy}</option>)}
      </select>
    </div>
  );
};
